/*
  <tat-product-reviews>: this product's reviews on the product page (sections/product-reviews.liquid), from
  Stamped's public widget API filtered by product id. Updates the product's average and count, then renders
  review cards (with customer photos) six at a time with "Show more". Long reviews get "Read more".
  The element starts hidden when the product has no reviews in Shopify's review metafields, and is shown
  if Stamped reports reviews after all. All review text is set with textContent.
*/
const API = 'https://stamped.io/api/widget/reviews';
const PHOTO_BASE = 'https://cdn.stamped.io/uploads/photos/';

class TatProductReviews extends HTMLElement {
  connectedCallback() {
    if (this.started || !this.dataset.apiKey || !this.dataset.productId) return;
    this.started = true;
    this.take = Number(this.dataset.take) || 6;
    this.page = 0;
    this.list = this.querySelector('[data-list]');
    this.querySelector('[data-more]')?.addEventListener('click', () => this.load());
    this.loadSummary();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '1000px 0px' }
    );
    // A hidden element never intersects, so watch the section instead.
    observer.observe(this.closest('section') || this);
  }

  // Straight away (not lazily): the product's average and count for the stars under the title and, when the
  // theme has no review metafields to work from, the Product structured data's aggregateRating.
  async loadSummary() {
    try {
      const params = new URLSearchParams({
        storeUrl: this.dataset.store,
        apiKey: this.dataset.apiKey,
        productId: this.dataset.productId,
        take: '1',
      });
      const response = await fetch(`${API}?${params}`);
      if (!response.ok) return;
      const data = await response.json();
      const total = Number(data.total) || 0;
      const average = Number(data.rating) || 0;
      if (!total || !average) return;

      const badge = document.querySelector('[data-live-rating]');
      if (badge) {
        badge.querySelectorAll('[data-live-star]').forEach((star) => {
          star.style.opacity = Number(star.dataset.liveStar) <= Math.round(average) ? '1' : '0.25';
        });
        badge.querySelector('[data-live-rating-text]').textContent = `${average.toFixed(1)} (${total.toLocaleString('en-AU')} ${total === 1 ? 'review' : 'reviews'})`;
        badge.setAttribute('aria-label', `Rated ${average.toFixed(1)} out of 5 from ${total} reviews`);
        badge.hidden = false;
      }

      const script = document.querySelector('script[data-product-jsonld]');
      if (script) {
        const json = JSON.parse(script.textContent);
        if (!json.aggregateRating) {
          json.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: average.toFixed(1),
            bestRating: '5',
            worstRating: '1',
            reviewCount: String(total),
          };
          script.textContent = JSON.stringify(json);
        }
      }
    } catch {
      // No rating shown.
    }
  }

  async load() {
    const button = this.querySelector('[data-more]');
    button?.setAttribute('aria-busy', 'true');
    try {
      const params = new URLSearchParams({
        storeUrl: this.dataset.store,
        apiKey: this.dataset.apiKey,
        productId: this.dataset.productId,
        take: String(this.take),
        page: String(this.page + 1),
      });
      const response = await fetch(`${API}?${params}`);
      if (!response.ok) return;
      const data = await response.json();
      const reviews = (data.data || []).filter((r) => (r.reviewMessage || '').trim());
      const total = Number(data.total) || 0;
      if (this.page === 0) {
        if (!total || !reviews.length) {
          this.hidden = true;
          return;
        }
        this.hidden = false;
        const average = Number(data.rating);
        if (average) this.querySelector('[data-product-average]').textContent = average.toFixed(1);
        this.querySelector('[data-product-count]').textContent = total.toLocaleString('en-AU');
        this.querySelector('[data-product-noun]').textContent = total === 1 ? 'review' : 'reviews';
      }
      this.page += 1;
      const cards = reviews.map((r) => this.card(r));
      this.list.append(...cards);
      requestAnimationFrame(() => cards.forEach((card) => this.clampCheck(card)));
      this.querySelector('[data-more-wrap]').hidden = this.page * this.take >= total;
    } catch {
      // Leave whatever is already shown.
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  clampCheck(card) {
    const text = card.querySelector('[data-field="text"]');
    const button = card.querySelector('[data-read-more]');
    if (!text || !button || text.scrollHeight <= text.clientHeight + 2) return;
    button.hidden = false;
    button.addEventListener('click', () => {
      const open = !text.hasAttribute('data-expanded');
      text.toggleAttribute('data-expanded', open);
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? 'Show less' : 'Read more';
    });
  }

  card(r) {
    const node = this.querySelector('template[data-card-template]').content.firstElementChild.cloneNode(true);
    const field = (name) => node.querySelector(`[data-field="${name}"]`);

    const stars = field('stars');
    const star = this.querySelector('template[data-star-template]').content;
    const rating = Math.max(1, Math.min(5, Number(r.reviewRating) || 5));
    stars.setAttribute('aria-label', `${rating} out of 5 stars`);
    for (let i = 0; i < rating; i++) stars.append(star.cloneNode(true));

    const date = new Date(r.dateCreated || r.reviewDate);
    if (!Number.isNaN(date.getTime())) {
      field('date').dateTime = date.toISOString().slice(0, 10);
      field('date').textContent = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    } else field('date').remove();

    const title = (r.reviewTitle || '').trim();
    if (title) field('title').textContent = title;
    else field('title').remove();
    field('text').textContent = r.reviewMessage.trim();

    const hidden = new Set((this.dataset.hiddenPhotos || '').split(',').filter(Boolean));
    const photos = String(r.reviewUserPhotos || '').split(',').map((p) => p.trim()).filter((p) => p && !hidden.has(p));
    if (photos.length) {
      field('photos').append(
        ...photos.slice(0, 4).map((p) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = PHOTO_BASE + p;
          a.dataset.reviewPhoto = '';
          a.dataset.caption = (r.reviewTitle || '').trim() || r.reviewMessage.trim();
          a.dataset.who = (r.author || 'Verified buyer').trim();
          a.className = 'block size-20 overflow-hidden rounded-xl bg-line';
          a.setAttribute('aria-label', 'Open customer photo');
          const img = document.createElement('img');
          img.src = PHOTO_BASE + p;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          img.className = 'size-full object-cover';
          a.append(img);
          li.append(a);
          return li;
        })
      );
    } else field('photos').remove();

    const name = (r.author || 'Verified buyer').trim();
    field('name').textContent = name;
    field('initial').textContent = name.charAt(0).toUpperCase();
    return node;
  }
}

if (!customElements.get('tat-product-reviews')) customElements.define('tat-product-reviews', TatProductReviews);
